import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { submitAgencyPickupRequest } from '@/lib/api/public'

const ITEMS = [
  { key: 'diapers', label: 'Diapers' },
  { key: 'hygiene', label: 'Hygiene products' },
  { key: 'wipes', label: 'Baby wipes' },
  { key: 'formula', label: 'Infant formula' },
  { key: 'breastfeeding', label: 'Breastfeeding resources' },
  { key: 'frida_pads', label: 'Frida postpartum pads' },
] as const

type ItemKey = typeof ITEMS[number]['key']

const AgencyPickupPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [items, setItems] = useState<Record<ItemKey, string>>({
    diapers: '', hygiene: '', wipes: '', formula: '', breastfeeding: '', frida_pads: '',
  })
  const [diaperSizes, setDiaperSizes] = useState('')
  const [organization, setOrganization] = useState('')
  const [organizationType, setOrganizationType] = useState('Small agency / nonprofit')
  const [contact, setContact] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [county, setCounty] = useState('')
  const [families, setFamilies] = useState('')
  const [notes, setNotes] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  const toggleItem = (key: ItemKey, value: string) => setItems(current => ({ ...current, [key]: value }))

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const requested = ITEMS.filter(item => Number(items[item.key]) > 0)
    if (!requested.length) {
      toast.error('Please enter a quantity for at least one resource.')
      return
    }
    if (!acknowledged) {
      toast.error('Please confirm the pickup details before submitting.')
      return
    }
    setLoading(true)
    try {
      await submitAgencyPickupRequest({
        organization_name: organization.trim(),
        organization_type: organizationType,
        contact_name: contact.trim(),
        email: email.trim(),
        phone: phone.trim(),
        county: county.trim(),
        estimated_families: families ? Number(families) : undefined,
        requested_items: requested.map(item => ({ name: item.label, quantity: Number(items[item.key]) })),
        diaper_sizes: diaperSizes.trim(),
        notes: notes.trim(),
      })
      setSubmitted(true)
    } catch (error) {
      console.error('[W.E.E. agency pickup]', error)
      toast.error('We could not submit your request. Please email info@womenempoweringeachother.org.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-amber-50 px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <Link to="/" className="text-sm font-semibold text-pink-700 hover:underline">← Back to W.E.E. Guided Access</Link>
        <header className="mt-6 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-pink-100 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-pink-700">W.E.E. Care Diaper Bank &amp; Humanitarian Resource Center</p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">Agency &amp; Doula Crisis Resource Pickup</h1>
          <p className="mt-3 text-gray-600">Supporting the agencies and doulas that support our communities. Small agencies and doulas may request available resources to help continue serving families.</p>
          <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="font-bold text-gray-900">Pickup: Thursday, October 29, 2026</p>
            <p className="mt-1 text-sm text-gray-700">1:00 PM – 4:00 PM</p>
            <p className="mt-2 text-sm text-gray-700">Please submit this form in advance so W.E.E. can review requests and prepare available items. Submission is a request only and does not guarantee item availability or fulfillment.</p>
          </div>
        </header>

        {submitted ? (
          <section className="mt-6 rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-pink-100" role="status">
            <div className="text-5xl" aria-hidden="true">💗</div>
            <h2 className="mt-3 text-2xl font-bold text-gray-900">Request received</h2>
            <p className="mt-3 text-gray-600">Thank you for submitting your agency pickup request. W.E.E. will review the requested resources and follow up using the contact information provided. Please wait for confirmation before assuming items are reserved.</p>
            <Link to="/" className="mt-6 inline-flex rounded-xl bg-pink-700 px-5 py-3 font-semibold text-white hover:bg-pink-800">Return to Guided Access</Link>
          </section>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-6 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-pink-100 sm:p-8">
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900">Agency / doula contact</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">Agency or practice name *
                  <input required value={organization} onChange={e => setOrganization(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
                <label className="block text-sm font-medium text-gray-700">Organization type *
                  <select required value={organizationType} onChange={e => setOrganizationType(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-3">
                    <option>Small agency / nonprofit</option><option>Doula / birth worker</option><option>Community-based organization</option><option>Other humanitarian provider</option>
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">Contact person *
                  <input required value={contact} onChange={e => setContact(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
                <label className="block text-sm font-medium text-gray-700">Email address *
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
                <label className="block text-sm font-medium text-gray-700">Phone number *
                  <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
                <label className="block text-sm font-medium text-gray-700">County served *
                  <input required value={county} onChange={e => setCounty(e.target.value)} placeholder="e.g. Leon, Gadsden, Wakulla" className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
                <label className="block text-sm font-medium text-gray-700 sm:col-span-2">Approximate number of families served (optional)
                  <input type="number" min="0" value={families} onChange={e => setFamilies(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
                </label>
              </div>
            </section>

            <section className="space-y-4">
              <div><h2 className="text-xl font-bold text-gray-900">Resources requested</h2><p className="mt-1 text-sm text-gray-600">Enter a quantity for each item you would like to request. Leave items blank if not needed. Quantities are requests and subject to available inventory.</p></div>
              <div className="grid gap-3 sm:grid-cols-2">
                {ITEMS.map(item => <label key={item.key} className="rounded-2xl border border-pink-100 bg-pink-50/50 p-4 text-sm font-semibold text-gray-800">{item.label}
                  <input type="number" min="0" step="1" inputMode="numeric" value={items[item.key]} onChange={e => toggleItem(item.key, e.target.value)} placeholder="Quantity requested" className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-3 font-normal" />
                </label>)}
              </div>
              <label className="block text-sm font-medium text-gray-700">Diaper sizes / specifications (if requesting diapers)
                <textarea value={diaperSizes} onChange={e => setDiaperSizes(e.target.value)} rows={2} placeholder="e.g. Newborn: 2 packs, Size 3: 4 packs; include preferred pack/case details" className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
              </label>
              <label className="block text-sm font-medium text-gray-700">Additional details or preparation notes (optional)
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Share any relevant item specifications or pickup coordination needs. Please do not include client names or private medical information." className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-3" />
              </label>
            </section>

            <label className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-700">
              <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} className="mt-1" />
              <span>I understand this is a resource request, not a confirmation of inventory or a reserved pickup. I will wait for W.E.E. to confirm the request and pickup details.</span>
            </label>
            <button type="submit" disabled={loading} className="w-full rounded-2xl bg-pink-700 px-6 py-4 font-bold text-white shadow-sm transition hover:bg-pink-800 disabled:cursor-not-allowed disabled:opacity-60">{loading ? 'Submitting request…' : 'Submit Agency Pickup Request'}</button>
            <p className="text-center text-xs text-gray-500">Questions? Email <a className="font-semibold text-pink-700 underline" href="mailto:info@womenempoweringeachother.org">info@womenempoweringeachother.org</a>.</p>
          </form>
        )}
        <footer className="py-6 text-center text-sm font-medium text-gray-500">Women Empowering Each Other, Inc. (W.E.E.) · DA.RE. — Diaper Assistance Recovery Efforts</footer>
      </div>
    </main>
  )
}

export default AgencyPickupPage
